/**
 * Vi visar aldrig den exakta adressen på kartan. Koordinaterna förskjuts
 * deterministiskt (samma annons hamnar alltid på samma plats) med ~150–500 m
 * så att köparen förstår området utan att få säljarens exakta position.
 */

export const MAP_PRIVACY_NOTE = "Kartan visar ungefärligt område – inte prylens exakta plats.";

function hash(seed: string): number {
  let h = 2166136261;
  for (let i = 0; i < seed.length; i++) {
    h ^= seed.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return (h >>> 0) / 4294967295;
}

/** Förskjuter en koordinat deterministiskt utifrån ett frö (t.ex. annonsens slug). */
export function jitterCoords(
  latitude: number,
  longitude: number,
  seed: string,
): { latitude: number; longitude: number } {
  const angle = hash(`${seed}:a`) * Math.PI * 2;
  const distance = 150 + hash(`${seed}:d`) * 350; // meter
  const dLat = (distance * Math.cos(angle)) / 111_320;
  const dLng =
    (distance * Math.sin(angle)) / (111_320 * Math.max(0.2, Math.cos((latitude * Math.PI) / 180)));
  return { latitude: latitude + dLat, longitude: longitude + dLng };
}

/**
 * Den position som ALLTID ska användas när en annons visas för någon annan –
 * i listan, på kartan och på annonssidan. Håller markören konsekvent.
 */
export function adDisplayCoords(ad: {
  slug: string;
  latitude?: number | null;
  longitude?: number | null;
}): { latitude: number; longitude: number } | null {
  if (ad.latitude == null || ad.longitude == null) return null;
  return jitterCoords(ad.latitude, ad.longitude, ad.slug);
}

/** Avstånd i kilometer mellan två punkter (Haversine). */
export function distanceKm(
  a: { latitude: number; longitude: number },
  b: { latitude: number; longitude: number },
): number {
  const R = 6371;
  const dLat = ((b.latitude - a.latitude) * Math.PI) / 180;
  const dLon = ((b.longitude - a.longitude) * Math.PI) / 180;
  const la1 = (a.latitude * Math.PI) / 180;
  const la2 = (b.latitude * Math.PI) / 180;
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(la1) * Math.cos(la2) * Math.sin(dLon / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(h));
}

/**
 * Flera deterministiska kandidatpunkter för samma annons, i fast ordning.
 * Används för att kunna hoppa vidare till nästa punkt om den första hamnar
 * i vatten (se `resolveLandCoords`).
 */
export function jitterCandidates(
  latitude: number,
  longitude: number,
  seed: string,
  count = 8,
): { latitude: number; longitude: number }[] {
  const out: { latitude: number; longitude: number }[] = [];
  for (let i = 0; i < count; i++) {
    out.push(jitterCoords(latitude, longitude, i === 0 ? seed : `${seed}#${i}`));
  }
  return out;
}
